import { Box } from "@mui/material";
import { isFulfilled, nanoid } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { NO_DRAG_SELECTOR } from "../../../app/constants";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { CustomButton } from "../../../common/components/functional/customButton/CustomButton";
import DialogHeader from "../../../common/components/functional/dialogs/DialogHeader";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { isoFormat } from "../../../common/helpers/formatDateTime";
import isEmpty from "../../../common/helpers/isEmpty";
import { SupplementsType } from "../../../common/types/apiTypes";
import { AutocompleteType } from "../../../common/types/autoComplete";
import { UnsavedChangesProvider } from "../../../contexts/UnsavedChangesContext";
import useIsLoading from "../../../hooks/useIsLoading";
import useMessage from "../../../hooks/useMessage";
import { fetchSkills } from "../../skills/skillsSlice";
import { SupplementsData } from "../../supplements/supplementAPI";
import { AccessRightContext } from "../../user/AccessRightProvider";
import { selectAllCustomerContracts } from "../customerContractsSlice";
import { FormContainer } from "../customerManagement/styles";
import {
  createOrderTemplate,
  selectOrderTemplateById,
  updateOrderTemplate,
  updateOrderTemplateActiveStatus,
} from "../orderTemplatesSlice";
import { ActionButtonsContainer, ModalContainer } from "../styles";
import { OrderTemplateFormValues } from "../types/types";
import CollectiveAgreement from "./CollectiveAgreement";
import { OrderTemplateOtherSettings } from "./OrderTemplateOtherSettings";
import OrderTemplateSupplementPriceOverrides from "./OrderTemplateSupplementPriceOverrides";
import OrderTemplateSupplements from "./OrderTemplateSupplements";
import RequiredInformation from "./RequiredInformation";
import Requirements from "./Requirements";
import SalaryLimits from "./SalaryLimits";
import Skills from "./Skills";

type Props = {
  orderTemplateId?: string;
  onClose: () => void;
};

const createOptions = (obj: Record<string, string>) =>
  Object.entries(obj).map(([id, name]) => ({
    id,
    name,
  }));

const formatAutocompleteDefaultValues = (
  array: string[],
  obj: Record<string, string>,
) =>
  Array.isArray(array) && !!array.length
    ? array.map((id) => ({
        id: id || "",
        name: id ? obj[id] : "",
      }))
    : null;

const OrderTemplateForm = ({ orderTemplateId, onClose }: Props) => {
  const [areTesVariationsValid, setAreTesVariationsValid] = useState(false);
  const [searchParams] = useSearchParams();
  const eezyCompanyId = searchParams.get("ecom");

  const [periodStartDateIsRequired, setPeriodStartDateIsRequired] = useState<
    boolean | null
  >(null);

  const { canEditOrderTemplate } = useContext(AccessRightContext);
  const canEdit = !!eezyCompanyId && canEditOrderTemplate(eezyCompanyId);

  const orderTemplate = useAppSelector((state) =>
    selectOrderTemplateById(state, orderTemplateId ?? ""),
  );

  const { sendMessage } = useMessage();
  const localized = useAppSelector((state) => state.localized);

  const isLoading = useIsLoading([
    "orderTemplates",
    "customerContracts",
    "skills",
  ]);

  const { i18n, t } = useTranslation();
  const dispatch = useAppDispatch();

  const { workTitle, certificate, training, skill } = useAppSelector(
    (state) => state.localized,
  );

  const contracts = useAppSelector((state) =>
    selectAllCustomerContracts(state),
  );

  const contractId =
    orderTemplate?.contractId ||
    contracts.find((contract) => contract.eezyCompany.id === eezyCompanyId)?.id;

  const certificateOptions = createOptions(certificate);
  const trainingOptions = createOptions(training);
  const [skillOptions, setSkillOptions] = useState<AutocompleteType[]>([]);

  const workTitleDefaultValue = orderTemplate?.workTitleId
    ? {
        name: workTitle[orderTemplate.workTitleId],
        id: orderTemplate.workTitleId,
      }
    : null;

  const tesDefaultValue = orderTemplate?.tesId
    ? {
        name: localized.tes[orderTemplate.tesId],
        id: orderTemplate.tesId,
      }
    : null;

  const dateDefaultValue =
    orderTemplate?.periodStartDate != null
      ? new Date(orderTemplate?.periodStartDate)
      : null;

  const certificateDefaultValues = formatAutocompleteDefaultValues(
    orderTemplate?.certificates as string[],
    certificate,
  );

  const trainingDefaultValues = formatAutocompleteDefaultValues(
    orderTemplate?.trainings as string[],
    training,
  );

  const skillsDefaultValues = formatAutocompleteDefaultValues(
    orderTemplate?.skills as string[],
    skill,
  );

  // add temporary id to edit languages
  const languageDefaultValues = orderTemplate?.languages.length
    ? orderTemplate.languages.map((language) => ({ ...language, id: nanoid() }))
    : [];

  let defaultSupplements: SupplementsData = Object.fromEntries(
    Object.entries(orderTemplate?.supplements || {}).map(([key, value]) => {
      return [key, { mode: value as string, definition: undefined }];
    }),
  );

  const defaultValues: OrderTemplateFormValues = {
    name: orderTemplate?.name || "",
    workTitle: workTitleDefaultValue,
    tes: tesDefaultValue,
    tesVariation: orderTemplate?.tesVariation || [],
    skills: skillsDefaultValues,
    languages: languageDefaultValues,
    certificates: certificateDefaultValues,
    trainings: trainingDefaultValues,
    salaryLimit: orderTemplate?.salaryLimit || "",
    salaryLimits: orderTemplate?.salaryLimits || [],
    periodStartDate: dateDefaultValue,
    supplements: defaultSupplements,
    supplementPriceOverrides: orderTemplate?.supplementPriceOverrides,
    isBillable: orderTemplate ? orderTemplate.isBillable : true,
  };

  const methods = useForm({ defaultValues, mode: "onChange" });
  const watchedTes = methods.watch("tes");
  const watchedTesVariation = methods.watch("tesVariation");

  useEffect(() => {
    // reset tesVariation and supplements if tes changes
    // block reseting when default value for tes is set
    if (methods.formState.dirtyFields?.tes) {
      methods.setValue("tesVariation", []);
      methods.setValue("supplements", {});
      methods.setValue("periodStartDate", null);
      setPeriodStartDateIsRequired(null);
      setAreTesVariationsValid(false);
    }
  }, [watchedTes, methods]);

  useEffect(() => {
    // reset periodStartDateIsRequired if tesVariation changes
    setPeriodStartDateIsRequired(null);
  }, [watchedTesVariation]);

  const handleStatusChange = () => {
    const params = {
      id: orderTemplateId as string,
      version: orderTemplate?.version as number,
      active: !orderTemplate?.active,
      eezyCompanyId: eezyCompanyId as string,
    };

    dispatch(updateOrderTemplateActiveStatus(params)).then((action) => {
      if (isFulfilled(action)) return onClose();
    });
  };

  const onSave = (data: OrderTemplateFormValues) => {
    const {
      supplements,
      tes,
      salaryLimit,
      workTitle,
      periodStartDate,
      ...rest
    } = data;

    const newSupplements: SupplementsType = Object.fromEntries(
      Object.entries(supplements).map((x) => {
        return [x[0], x[1].mode];
      }),
    );

    if (contractId && workTitle?.id && tes?.id) {
      const normalizedSalaryLimits = data.salaryLimits ?? [];
      const hasSalaryLimits = normalizedSalaryLimits.length > 0;
      const createOrderRowTemplate = {
        ...rest,
        contractId,
        eezyCompanyId: eezyCompanyId as string,
        skills: data?.skills?.map((skill) => skill.id) || [],
        trainings: data?.trainings?.map((training) => training.id) || [],
        certificates:
          data?.certificates?.map((certificate) => certificate.id) || [],
        languages: data.languages || [],
        workTitleId: workTitle.id,
        tesId: tes.id,
        salaryLimit: hasSalaryLimits ? null : salaryLimit || null,
        salaryLimits: hasSalaryLimits ? normalizedSalaryLimits : [],
        active: true,
        supplements: newSupplements,
        periodStartDate:
          periodStartDate != null ? format(periodStartDate!!, isoFormat) : null,
        isBillable: data?.isBillable as boolean,
      };

      const updateOrderRowTemplate = {
        ...createOrderRowTemplate,
        eezyCompanyId: eezyCompanyId as string,
        id: orderTemplate?.id as string,
        version: orderTemplate?.version as number,
        active: orderTemplate?.active as boolean,
        supplements: newSupplements,
        periodStartDate:
          periodStartDate != null ? format(periodStartDate!!, isoFormat) : null,
        isBillable: data?.isBillable as boolean,
      };

      if (orderTemplateId) {
        dispatch(updateOrderTemplate(updateOrderRowTemplate)).then((action) => {
          if (isFulfilled(action)) {
            onClose();
          }
        });
      } else {
        dispatch(createOrderTemplate(createOrderRowTemplate)).then((action) => {
          if (isFulfilled(action)) {
            onClose();
          }
        });
      }
    } else {
      sendMessage(t("errors:internal_error"), "error");
    }
  };

  const handleAreTesVariationsValidChange = (isValid: boolean) => {
    setAreTesVariationsValid(isValid);
  };

  const activeStatusLabel = orderTemplate?.active
    ? t("customers:disable")
    : t("common:active");

  // react-hook-form issue: isValid is sometimes false despite no errors #2755
  // hack it by reading the value before using it
  const isValid = methods.formState.isValid;

  // Wait for variation specific settings before allowing save
  const isVariationSettingsPending =
    areTesVariationsValid && periodStartDateIsRequired === null;
  const isPeriodStartDateMissing =
    periodStartDateIsRequired === true && !methods.watch("periodStartDate");

  const saveDisabled =
    !methods.formState.isDirty ||
    !isEmpty(methods.formState.errors) ||
    !isValid ||
    methods.formState.isSubmitting ||
    isLoading ||
    (!!orderTemplateId && !orderTemplate?.active) ||
    !areTesVariationsValid ||
    isVariationSettingsPending ||
    isPeriodStartDateMissing;

  const title = methods.watch("name");
  const formWorkTitle = methods.watch("workTitle");

  useEffect(() => {
    if (!formWorkTitle || !formWorkTitle.id) return;
    const language = i18n.language.substring(0, 2);
    dispatch(
      fetchSkills({
        language,
        workTitleIds: formWorkTitle.id,
      }),
    )
      .unwrap()
      .then((skills) => {
        const filteredSkills = skills
          .sort((a, b) => a.fieldOfWorkId.localeCompare(b.fieldOfWorkId))
          .map((s) => ({
            id: s.id,
            name: skill[s.id],
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setSkillOptions(filteredSkills);
      });
  }, [dispatch, i18n.language, skill, formWorkTitle]);

  return (
    <ModalContainer sx={{ cursor: "auto" }}>
      <FormProvider {...methods}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <DialogHeader title={title} onClose={onClose} />
          <FormContainer maxWidth="931px" className={NO_DRAG_SELECTOR}>
            <OverlaySpinner
              sx={{ borderRadius: "12px" }}
              isLoading={isLoading}
            />
            <Box>
              <RequiredInformation />
              <CollectiveAgreement
                edit={!!orderTemplateId}
                handleAreTesVariationsValidChange={
                  handleAreTesVariationsValidChange
                }
                contractId={contractId}
              />
              <Requirements
                certificateOptions={certificateOptions}
                trainingOptions={trainingOptions}
              />
              <Skills skillOptions={skillOptions} />
              <SalaryLimits
                periodStartDateIsRequired={periodStartDateIsRequired}
              />
              <OrderTemplateSupplements
                areTesVariationsValid={areTesVariationsValid}
                setPeriodStartDateIsRequired={setPeriodStartDateIsRequired}
              />
              <OrderTemplateSupplementPriceOverrides />
              <OrderTemplateOtherSettings />
            </Box>
          </FormContainer>
        </UnsavedChangesProvider>
      </FormProvider>
      <ActionButtonsContainer marginTop="1rem">
        <CustomButton
          accessRightChecks={[canEdit]}
          disabled={saveDisabled}
          variant="contained"
          sx={{ borderRadius: "18px", height: "36px" }}
          onClick={methods.handleSubmit(onSave)}
        >
          {t("common:save")}
        </CustomButton>
        <CustomButton
          accessRightChecks={[canEdit]}
          variant="outlined"
          color={!orderTemplate?.active ? "primary" : "error"}
          sx={{
            borderRadius: "18px",
            height: "36px",
            visibility: orderTemplateId ? "visible" : "hidden",
          }}
          onClick={handleStatusChange}
        >
          {activeStatusLabel}
        </CustomButton>
      </ActionButtonsContainer>
    </ModalContainer>
  );
};
export default OrderTemplateForm;
